/**
 * QuestSubTab.tsx
 *
 * Quest Diagnostics orders/results matching legacy QUEST tab.
 * Columns mirror the Documents tab: Collect Date, Result Date, Test, Status.
 * Every column is sortable via the shared SortableHeader (same pattern as the
 * Documents tab). Row click loads the full result detail in the patient-detail
 * right sidebar (with an expand-to-modal affordance) via onPreview.
 */
import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Badge, Spinner } from '@/components/ui';
import type { LabResult } from '@/services/labs.service';
import type { LabPreviewTarget } from './LabPdfPreviewPanel';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

type SortKey = 'collectDate' | 'resultDate' | 'test' | 'status';
type SortDir = 'asc' | 'desc';

/** Display status text (mirrors the Status cell) so sorting matches what's shown. */
function statusText(r: LabResult): string {
  if (r.orcStatus) return r.orcStatus;
  return r.orderFound ? 'Received' : 'Pending';
}

/** Comparison value for a row + sort key. Dates stay ISO so they sort chronologically. */
function sortValue(r: LabResult, key: SortKey): string {
  switch (key) {
    case 'collectDate': return r.collectedAt ?? r.orderedAt ?? '';
    case 'resultDate': return r.resultedAt ?? r.orderedAt ?? '';
    case 'test': return r.testsSummary ?? '';
    case 'status': return statusText(r);
  }
}

interface QuestSubTabProps {
  results: LabResult[];
  loading: boolean;
  onPreview: (target: LabPreviewTarget) => void;
  onSelectResult?: (result: LabResult) => void;
}

export function QuestSubTab({ results, loading, onPreview, onSelectResult }: QuestSubTabProps) {
  const [sortBy, setSortBy] = useState<SortKey>('resultDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const questResults = useMemo(() => {
    const rows = results.filter((r) => r.source === 'quest');
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortBy);
      const bv = sortValue(b, sortBy);
      // Empty values always sort last regardless of direction.
      if (av === '' && bv === '') return 0;
      if (av === '') return 1;
      if (bv === '') return -1;
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [results, sortBy, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'test' || key === 'status' ? 'asc' : 'desc');
    }
  }

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  if (questResults.length === 0) {
    return <p className="py-4 text-sm text-slate-400 italic">No Quest Diagnostics results found.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <SortableHeader label="Collect Date" sortKey="collectDate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[15%] whitespace-nowrap" />
            <SortableHeader label="Result Date" sortKey="resultDate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[15%] whitespace-nowrap" />
            <SortableHeader label="Test" sortKey="test" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            <SortableHeader label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[15%]" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {questResults.map((r) => {
            const orderIdNum = r.orderNumber ? Number(r.orderNumber) : 0;
            return (
              <tr
                key={r.rowKey}
                className="hover:bg-slate-50 cursor-pointer align-top"
                onClick={() => {
                  onPreview({
                    kind: 'quest-detail',
                    labResultId: r.labResultId,
                    orderId: r.labResultId === 0 && orderIdNum > 0 ? orderIdNum : undefined,
                    title: r.orderNumber ? `Quest Order #${r.orderNumber}` : 'Quest Result',
                  });
                  onSelectResult?.(r);
                }}
              >
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(r.collectedAt ?? r.orderedAt)}</td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(r.resultedAt ?? r.orderedAt)}</td>
                <td className="px-4 py-3 text-slate-800 [overflow-wrap:anywhere] min-w-0">{r.testsSummary ?? '—'}</td>
                <td className="px-4 py-3">
                  {r.orcStatus ? (
                    <Badge
                      variant={r.orcStatus === 'Complete' ? 'success' : r.orcStatus === 'Cancelled' ? 'error' : r.orcStatus.includes('Process') ? 'warning' : 'neutral'}
                      size="sm"
                    >
                      {r.orcStatus}
                    </Badge>
                  ) : r.orderFound ? (
                    <Badge variant="info" size="sm">Received</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">Pending</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  sortBy,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <th className={`px-4 py-3 ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
          active ? 'text-slate-700' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
        )}
      </button>
    </th>
  );
}
